import classNames from 'classnames';
import camelCase from 'lodash-es/camelCase';
import { MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';

import { useDocumentUrlGenerator } from '../../hooks/useUrlGenerator';
import getRatio from '../../lib/getRatio';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import DocumentImage from '../partials/DocumentImage';
import Time from '../partials/Time';
import HorizontalCard from './HorizontalCard';

import styles from '../../styles/cards/notification-card.module.css';

export interface NotificationCommentReplyCardProps {
    comment: Comment | null;
    reply: Comment | null;
    date: string | null;
    theme?: CardTheme | LegacyCardTheme | null;
    size?: CardSize | null;
    ratio?: CardRatio | null;
    height?: number | null;
    withoutTime?: boolean;
    className?: string | null;
    onClick?: MouseEventHandler<HTMLAnchorElement> | null;
}

function NotificationCommentReplyCard({
    comment = null,
    reply = null,
    date = null,
    theme: providedTheme = null,
    size = null,
    ratio = null,
    height = null,
    withoutTime = false,
    className = null,
    onClick = null,
}: NotificationCommentReplyCardProps) {
    const theme = normalizeCardTheme(providedTheme);
    const thumbnailRatio = getRatio(ratio, 1);

    const { document } = comment || {};
    const { author } = reply || {};
    const { name } = author || {};
    const { title, image = null } = document || {};
    const isBig = size === 'big';
    const isSmall = size === 'small';
    const isTiny = size === 'tiny';
    let defaultHeight = 100;
    if (isBig) {
        defaultHeight = 150;
    } else if (isSmall) {
        defaultHeight = 70;
    } else if (isTiny) {
        defaultHeight = 50;
    }

    const documentUrl = useDocumentUrlGenerator();
    const url = documentUrl(document);

    const finalHeight = height || defaultHeight;
    return (
        <HorizontalCard
            theme={theme || 'none'}
            href={url}
            onClick={onClick}
            thumbnail={
                image !== null ? (
                    <DocumentImage
                        media={image}
                        className={styles.image}
                        type="comment"
                        withIcon
                        iconClassName={styles.thumbnailIcon}
                    />
                ) : null
            }
            thumbnailRatio={thumbnailRatio}
            height={finalHeight}
            labelClassName={styles.label}
            className={classNames([styles.container, styles[camelCase(size)], className])}
        >
            <div className={styles.message}>
                <FormattedMessage
                    defaultMessage="<strong>{name}</strong> a répondu à votre commentaire sur « <strong>{title}</strong> »."
                    description="Notification message"
                    values={{ name, title }}
                />
            </div>
            {!withoutTime ? <Time date={date} className={styles.time} /> : null}
        </HorizontalCard>
    );
}

export default NotificationCommentReplyCard;
